import React, { useCallback, useEffect, useState } from "react";
import {
  ArchiveRestore, CheckCircle2, CopyX, Download, Eraser, FileSpreadsheet, Handshake,
  IndianRupee, Trash2, XCircle, Search, RotateCcw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, fmtINR, fmtDate, downloadBlob, getAuth } from "../../lib/api";
import { StaffLabel, useStaffLabel } from "../../components/StaffAvatar";
import { PaginationBar, TableRowsSkeleton } from "../../components/PaginationBar";
import { UndoImportDialog } from "../../components/UndoImportDialog";
import { BatchLogSelectionBar, DeleteBatchLogsDialog } from "../../components/DeleteBatchLogsDialog";
import { deleteBatchLogs, logKey, parseLogKey, useBatchLogAccess } from "../../lib/batchLogs";
import { ReadOnlyNotice } from "../../components/FeatureGate";
import { ImportReconciliationDialog, ReconcileButton } from "../../components/upload/ImportReconciliationDialog";   // Phase 212

const STATUS_META = {
  created: { label: "Created", icon: CheckCircle2, cls: "text-emerald-700", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  duplicate: { label: "Duplicate", icon: CopyX, cls: "text-amber-700", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  failed: { label: "Failed", icon: XCircle, cls: "text-red-700", chip: "bg-red-50 text-red-800 border-red-200" },
};

// Batch (job) status chips — "cancelled" reads as "Stopped" for admins.
const BATCH_STATUS = {
  completed: { label: "Completed", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  processing: { label: "Processing", chip: "bg-primary/10 text-primary border-primary/20" },
  cancelled: { label: "Stopped", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  failed: { label: "Failed", chip: "bg-red-50 text-red-800 border-red-200" },
};

function BatchStatusBadge({ batch }) {
  const meta = BATCH_STATUS[batch.status] || { label: batch.status, chip: "" };
  return (
    <Badge variant="outline" className={`text-[10px] ${meta.chip}`} title={batch.error || undefined}
      data-testid={`batch-status-${batch.upload_id}`}>
      {meta.label}
    </Badge>
  );
}

function SourceBadge({ source }) {
  return source === "third_party" ? (
    <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200 gap-0.5">
      <Handshake className="h-2.5 w-2.5" /> Third Party
    </Badge>
  ) : (
    <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-800 border-sky-200 gap-0.5">
      <FileSpreadsheet className="h-2.5 w-2.5" /> Excel
    </Badge>
  );
}

export default function UploadStatus({ code, readOnly = false }) {
  const isSuper = getAuth()?.user?.role === "super_admin";   // Phase 192: only the Super Admin may restore deleted properties
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState(null);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchPage, setBatchPage] = useState(1);
  const [batchLimit, setBatchLimit] = useState(25);
  const [batchLoading, setBatchLoading] = useState(false);
  const [nd, setNd] = useState(null);            // no-demand properties
  const [ndSearch, setNdSearch] = useState("");
  const [ndPage, setNdPage] = useState(1);
  const [ndSelected, setNdSelected] = useState({});
  const [trash, setTrash] = useState(null);
  const [trashTotal, setTrashTotal] = useState(0);
  const [trashPage, setTrashPage] = useState(1);
  const [trashLimit, setTrashLimit] = useState(25);
  const [trashLoading, setTrashLoading] = useState(false);
  const [trashSelected, setTrashSelected] = useState({});
  const [confirm, setConfirm] = useState(null);  // {type: "selected"|"all"}
  const [confirmClearHistory, setConfirmClearHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [undoJob, setUndoJob] = useState(null);  // third-party batch being rolled back
  const [recon, setRecon] = useState(null);      // Phase 212: {source, upload_id, filename} → Import Reconciliation Report
  // Phase 151: remove batch records from this list (log entry only — properties/demands stay).
  const { canDelete: canDeleteLogsRaw } = useBatchLogAccess(code);
  const canDeleteLogs = canDeleteLogsRaw && !readOnly;   // Phase 152: "View only" never deletes batch records
  const [logSelected, setLogSelected] = useState(() => new Set());
  const [logDeleteOpen, setLogDeleteOpen] = useState(false);
  useEffect(() => { setLogSelected(new Set()); }, [batchPage, batchLimit]);

  const loadSummary = useCallback(() => api.get(`/t/${code}/upload-reports/summary`)
    .then((r) => setSummary(r.data)).catch(() => {}), [code]);
  const loadBatches = useCallback(() => {
    setBatchLoading(true);
    return api.get(`/t/${code}/upload-reports/batches`, { params: { page: batchPage, limit: batchLimit } })
      .then((r) => {
        const d = r.data;
        setBatches(Array.isArray(d) ? d : d.items);
        setBatchTotal(Array.isArray(d) ? d.length : d.total);
      }).catch(() => {}).finally(() => setBatchLoading(false));
  }, [code, batchPage, batchLimit]);
  const loadNd = useCallback(() => api.get(`/t/${code}/upload-reports/no-demand`,
    { params: { search: ndSearch || undefined, page: ndPage, limit: 25 } })
    .then((r) => { setNd(r.data); setNdSelected({}); }).catch(() => {}), [code, ndSearch, ndPage]);
  const loadTrash = useCallback(() => {
    setTrashLoading(true);
    return api.get(`/t/${code}/upload-reports/trash`, { params: { page: trashPage, limit: trashLimit } })
      .then((r) => {
        const d = r.data;
        setTrash(Array.isArray(d) ? d : d.items);
        setTrashTotal(Array.isArray(d) ? d.length : d.total);
        setTrashSelected({});
      }).catch(() => {}).finally(() => setTrashLoading(false));
  }, [code, trashPage, trashLimit]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadBatches(); }, [loadBatches]);
  useEffect(() => { loadTrash(); }, [loadTrash]);
  useEffect(() => { loadNd(); }, [loadNd]);

  const reloadAll = () => { loadSummary(); loadBatches(); loadNd(); loadTrash(); };

  const logSelectable = (batches || []).filter((b) => b.log_deletable);
  const logAll = logSelectable.length > 0 && logSelectable.every((b) => logSelected.has(logKey(b.log_kind, b.upload_id)));
  const logSome = logSelectable.some((b) => logSelected.has(logKey(b.log_kind, b.upload_id)));
  const toggleLogAll = (on) => setLogSelected((prev) => { const n = new Set(prev); logSelectable.forEach((b) => { const k = logKey(b.log_kind, b.upload_id); if (on) n.add(k); else n.delete(k); }); return n; });
  const toggleLogOne = (b, on) => setLogSelected((prev) => { const n = new Set(prev); const k = logKey(b.log_kind, b.upload_id); if (on) n.add(k); else n.delete(k); return n; });
  const logSelectedItems = [...logSelected].map((k) => { const { kind, id } = parseLogKey(k); return { kind, id, filename: (batches || []).find((b) => b.upload_id === id)?.filename }; });

  // One export at a time per button; large registers (150k+ rows) take several seconds, so the
  // icon shows a spinner and a persistent "Preparing…" toast until the file actually lands.
  const [exporting, setExporting] = useState(null);
  const runExport = async (key, path, filename, doneMsg) => {
    if (exporting) return;
    setExporting(key);
    const tid = toast.loading("Preparing Excel… large registers can take up to a minute", { id: `export-${key}` });
    try {
      await downloadBlob(path, filename);
      toast.success(doneMsg, { id: tid });
    } catch (e) {
      toast.error(`Export failed: ${errMsg(e)}`, { id: tid, action: { label: "Retry", onClick: () => runExport(key, path, filename, doneMsg) } });
    } finally {
      setExporting(null);
    }
  };

  const exportRows = (status, uploadId) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (uploadId) params.set("upload_id", uploadId);
    return runExport(`rows-${status || "all"}-${uploadId || "all"}`,
      `/t/${code}/upload-reports/rows/export?${params.toString()}`, `upload-rows_${status || "all"}.xlsx`, "Excel downloaded");
  };

  const exportRaised = () => runExport("raised", `/t/${code}/upload-reports/demands-raised/export`,
    "raised-demands.xlsx", "Raised demands exported");

  const exportNoDemand = () => {
    const params = ndSearch ? `?search=${encodeURIComponent(ndSearch)}` : "";
    return runExport("no-demand", `/t/${code}/upload-reports/no-demand/export${params}`,
      "properties-without-demand.xlsx", "Excel downloaded");
  };
  const ExportIcon = ({ active, className = "h-4 w-4" }) => active ? <Loader2 className={`${className} animate-spin`} /> : <Download className={className} />;

  const doDelete = async () => {
    setBusy(true);
    try {
      const body = confirm.type === "all"
        ? { all: true, search: ndSearch || null }
        : { property_ids: Object.keys(ndSelected).filter((k) => ndSelected[k]) };
      const r = await api.post(`/t/${code}/upload-reports/no-demand/delete`, body);
      toast.success(`${r.data.deleted} propert${r.data.deleted === 1 ? "y" : "ies"} moved to the recycle bin — restorable anytime`);
      setConfirm(null);
      reloadAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doRestore = async (all = false) => {
    setBusy(true);
    try {
      const body = all ? { all: true } : { trash_ids: Object.keys(trashSelected).filter((k) => trashSelected[k]) };
      if (!all && body.trash_ids.length === 0) { toast.error("Select the deleted properties to restore"); setBusy(false); return; }
      const r = await api.post(`/t/${code}/upload-reports/trash/restore`, body);
      let msg = `${r.data.restored} propert${r.data.restored === 1 ? "y" : "ies"} restored`;
      if (r.data.pin_conflicts?.length) msg += ` • skipped (PIN now in use): ${r.data.pin_conflicts.join(", ")}`;
      toast.success(msg);
      reloadAll();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const ndSelectedCount = Object.values(ndSelected).filter(Boolean).length;
  const rows = summary?.rows;

  const doClearHistory = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/t/${code}/upload-reports/batches`);
      const d = r.data || {};
      const total = (d.excel_batches || 0) + (d.third_party_jobs || 0);
      toast.success(`Upload history cleared — ${total} batch${total === 1 ? "" : "es"}, ${d.row_results || 0} row records and ${d.stored_files || 0} stored file${d.stored_files === 1 ? "" : "s"} removed. Properties and demands were not affected.`);
      setConfirmClearHistory(false);
      loadSummary(); loadBatches();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      {readOnly && <ReadOnlyNotice what="upload status, batches and the recycle bin" testId="upload-status-readonly-notice" />}
      {/* ---- GLOBAL SUMMARY ---- */}
      {!summary ? <Spinner /> : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {["created", "duplicate", "failed"].map((k) => {
            const m = STATUS_META[k];
            const Icon = m.icon;
            return (
              <Card key={k} className="shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Icon className={`h-3.5 w-3.5 ${m.cls}`} /> {m.label}
                      </p>
                      <p className="text-2xl font-semibold tabular-nums mt-1" data-testid={`upload-summary-${k}`}>{rows[k]}</p>
                      <p className="text-[11px] text-muted-foreground">rows across all uploads</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" title={`Export ${m.label} rows`}
                      onClick={() => exportRows(k)} disabled={!!exporting} aria-busy={exporting === `rows-${k}-all`}
                      data-testid={`export-${k}-button`}>
                      <ExportIcon active={exporting === `rows-${k}-all`} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <IndianRupee className="h-3.5 w-3.5 text-primary" /> Demands Raised
                  </p>
                  <p className="text-2xl font-semibold tabular-nums mt-1" data-testid="upload-summary-raised">{summary.demands_raised.count}</p>
                  <p className="text-[11px] text-muted-foreground">{fmtINR(summary.demands_raised.total)} total demand</p>
                </div>
                <Button variant="ghost" size="icon" className="h-8 w-8" title="Export raised demands"
                  onClick={exportRaised} disabled={!!exporting} aria-busy={exporting === "raised"}
                  data-testid="export-raised-demands-button">
                  <ExportIcon active={exporting === "raised"} />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ---- BATCH HISTORY ---- */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-sm">Upload batch history</p>
              <p className="text-[11px] text-muted-foreground">Click a count's icon to export those rows for that batch · Reconcile compares the file with the database</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {canDeleteLogs && batches && batchTotal > 0 && (
                <BatchLogSelectionBar count={logSelected.size} onClear={() => setLogSelected(new Set())} onDelete={() => setLogDeleteOpen(true)}
                  disabled={batchLoading} testId="upload-batches" />
              )}
              {batches && batchTotal > 0 && !readOnly && (
                <Button variant="outline" size="sm"
                  className="shrink-0 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirmClearHistory(true)} data-testid="clear-upload-history-button">
                  <Eraser className="h-3.5 w-3.5 mr-1.5" /> Clear history
                </Button>
              )}
            </div>
          </div>
          {!batches ? <TableRowsSkeleton rows={5} cols={7} testId="batches-skeleton" /> : batches.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4" data-testid="batches-empty">No uploads yet</p>
          ) : (
            <div className={`overflow-x-auto ${batchLoading ? "opacity-60 transition-opacity" : ""}`}>
              <Table data-testid="upload-batches-table">
                <TableHeader>
                  <TableRow>
                    {canDeleteLogs && (
                      <TableHead className="w-10">
                        <Checkbox checked={logAll ? true : logSome ? "indeterminate" : false} onCheckedChange={(on) => toggleLogAll(on === true)}
                          disabled={logSelectable.length === 0} aria-label="Select all batches on this page" data-testid="upload-batches-select-all" />
                      </TableHead>
                    )}
                    <TableHead>Date</TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Created</TableHead>
                    <TableHead className="text-center">Duplicate</TableHead>
                    <TableHead className="text-center">Failed</TableHead>
                    <TableHead className="text-center">Demands</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((b, i) => (
                    <TableRow key={b.upload_id} className={logSelected.has(logKey(b.log_kind, b.upload_id)) ? "bg-primary/5" : ""}
                      data-state={logSelected.has(logKey(b.log_kind, b.upload_id)) ? "selected" : undefined} data-testid={`batch-row-${i}`}>
                      {canDeleteLogs && (
                        <TableCell className="w-10">
                          <Checkbox checked={logSelected.has(logKey(b.log_kind, b.upload_id))} onCheckedChange={(on) => toggleLogOne(b, on === true)}
                            disabled={!b.log_deletable} aria-label={`Select batch ${b.filename || b.upload_id}`}
                            title={b.log_deletable ? undefined : "Still processing — cannot be removed yet"}
                            data-testid={`upload-batches-select-${b.upload_id}`} />
                        </TableCell>
                      )}
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate((b.created_at || "").slice(0, 10))}</TableCell>
                      <TableCell>
                        <p className="text-sm truncate max-w-[220px]" title={b.filename}>{b.filename || "-"}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <SourceBadge source={b.source} />
                          {b.fy_label && <span className="text-[10px] text-muted-foreground">{b.fy_label}</span>}
                          {b.by && <span className="text-[10px] text-muted-foreground truncate max-w-[140px]" title={b.by}>{b.by}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <BatchStatusBadge batch={b} />
                          {b.undo?.status === "completed" && (
                            <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-800 border-violet-200 gap-0.5"
                              title={`Rolled back by ${b.undo.by_name || "admin"}`} data-testid={`batch-undone-${b.upload_id}`}>
                              <ArchiveRestore className="h-2.5 w-2.5" /> Rolled back · {b.undo.moved}
                            </Badge>
                          )}
                          {b.undo?.status === "processing" && (
                            <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20"
                              data-testid={`batch-undo-running-${b.upload_id}`}>Undo running…</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm tabular-nums">
                        {b.total_rows ?? "-"}
                        {b.status === "cancelled" && b.total_rows != null && (
                          <p className="text-[10px] text-muted-foreground" data-testid={`batch-processed-${b.upload_id}`}>{b.processed ?? 0} processed</p>
                        )}
                      </TableCell>
                      {["created", "duplicate", "failed"].map((k) => (
                        <TableCell key={k} className="text-center">
                          <span className={`inline-flex items-center gap-1 text-sm tabular-nums font-medium ${STATUS_META[k].cls}`}>
                            {b.counts?.[k] ?? "-"}
                            {b.has_row_results && (b.counts?.[k] || 0) > 0 && (
                              <button className="text-muted-foreground hover:text-foreground" title={`Export ${k} rows`}
                                onClick={() => exportRows(k, b.upload_id)} data-testid={`batch-${i}-export-${k}`}>
                                <Download className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        </TableCell>
                      ))}
                      <TableCell className="text-center text-sm tabular-nums">{b.counts?.demands_raised ?? "-"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {(b.source === "third_party" ? ["completed", "cancelled", "failed"].includes(b.status) && (b.has_row_results || (b.counts?.created || 0) + (b.counts?.failed || 0) + (b.counts?.duplicate || 0) > 0)
                          : b.status === "completed") && (
                          <ReconcileButton onClick={() => setRecon({ source: b.source, upload_id: b.upload_id, filename: b.filename })}
                            className="h-7 text-xs" testId={`batch-${i}-reconcile-button`} />
                        )}
                        {b.source === "third_party" && b.status !== "processing" && (b.counts?.created || 0) > 0
                          && b.undo?.status !== "completed" && b.undo?.status !== "processing" && (
                          b.undo_eligibility?.reason === "expired" ? (
                            <span className="text-[10px] text-muted-foreground" title={b.undo_eligibility.reason_text}
                              data-testid={`batch-${i}-undo-expired`}>Undo window closed</span>
                          ) : readOnly ? (
                            <span className="text-[10px] text-muted-foreground" title="View only — undo needs full access"
                              data-testid={`batch-${i}-undo-readonly`}>Undo (view only)</span>
                          ) : (
                          <Button variant="outline" size="sm" onClick={() => setUndoJob(b)}
                            className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                            data-testid={`batch-${i}-undo-button`}>
                            <RotateCcw className="h-3 w-3 mr-1" /> Undo import
                          </Button>
                          )
                        )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {batches && batchTotal > 0 && (
            <div className="px-4 pb-3">
              <PaginationBar page={batchPage} limit={batchLimit} total={batchTotal} loading={batchLoading}
                onPageChange={setBatchPage} onLimitChange={(n) => { setBatchLimit(n); setBatchPage(1); }} testId="batches-pagination" />
            </div>
          )}
        </CardContent>
      </Card>
      <UndoImportDialog code={code} job={undoJob} open={!!undoJob} onOpenChange={(v) => { if (!v) setUndoJob(null); }}
        onDone={() => reloadAll()} />
      {recon && (
        <ImportReconciliationDialog code={code} source={recon.source} batchId={recon.upload_id} filename={recon.filename}
          open={!!recon} onOpenChange={(v) => { if (!v) setRecon(null); }} testIdPrefix="batch-recon" />
      )}
      {canDeleteLogs && (
        <DeleteBatchLogsDialog open={logDeleteOpen} onOpenChange={setLogDeleteOpen} items={logSelectedItems}
          request={(its) => deleteBatchLogs(code, its)} onDone={() => { setLogSelected(new Set()); loadBatches(); }} testId="upload-batches-delete" />
      )}

      {/* ---- NO-DEMAND PROPERTIES ---- */}
      <Card className="shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="font-medium text-sm" data-testid="no-demand-title">
                Properties without any demand {nd ? `(${nd.total})` : ""}
              </p>
              <p className="text-[11px] text-muted-foreground">
                No tax raised yet — export, or delete them to the recycle bin and re-upload a corrected file
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={exportNoDemand} disabled={!!exporting} aria-busy={exporting === "no-demand"}
                data-testid="no-demand-export-button">
                <ExportIcon active={exporting === "no-demand"} className="h-3.5 w-3.5 mr-1.5" /> {exporting === "no-demand" ? "Preparing…" : "Export"}
              </Button>
              {!readOnly && <>
              <Button variant="outline" size="sm" disabled={ndSelectedCount === 0}
                className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setConfirm({ type: "selected" })} data-testid="no-demand-delete-selected-button">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete Selected ({ndSelectedCount})
              </Button>
              <Button variant="outline" size="sm" disabled={!nd || nd.total === 0}
                className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setConfirm({ type: "all" })} data-testid="no-demand-delete-all-button">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete All
              </Button>
              </>}
            </div>
          </div>
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 h-9" placeholder="Search PIN, owner, mobile..." value={ndSearch}
              onChange={(e) => { setNdSearch(e.target.value); setNdPage(1); }} data-testid="no-demand-search-input" />
          </div>
          {!nd ? <Spinner /> : nd.items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center" data-testid="no-demand-empty">
              Every property in the register has at least one demand raised.
            </p>
          ) : (
            <>
              <div className="border rounded-lg overflow-x-auto">
                <Table data-testid="no-demand-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox checked={nd.items.length > 0 && nd.items.every((p) => ndSelected[p.id])}
                          onCheckedChange={(v) => {
                            const next = {};
                            nd.items.forEach((p) => { next[p.id] = !!v; });
                            setNdSelected(next);
                          }} data-testid="no-demand-select-all" />
                      </TableHead>
                      <TableHead>PIN</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead className="hidden md:table-cell">Ward</TableHead>
                      <TableHead className="hidden md:table-cell">Source</TableHead>
                      <TableHead className="hidden lg:table-cell">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nd.items.map((p, i) => (
                      <TableRow key={p.id} data-testid={`no-demand-row-${i}`}>
                        <TableCell>
                          <Checkbox checked={!!ndSelected[p.id]}
                            onCheckedChange={(v) => setNdSelected((s) => ({ ...s, [p.id]: !!v }))}
                            data-testid={`no-demand-check-${i}`} />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{p.pin}</TableCell>
                        <TableCell className="text-sm">{p.owner_name || "-"}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm">{p.ward_label || "-"}</TableCell>
                        <TableCell className="hidden md:table-cell"><SourceBadge source={p.source === "third_party" ? "third_party" : "excel"} /></TableCell>
                        <TableCell className="hidden lg:table-cell text-xs">{fmtDate((p.created_at || "").slice(0, 10))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Page {ndPage} • {nd.total} total</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={ndPage === 1} onClick={() => setNdPage(ndPage - 1)}>Previous</Button>
                  <Button variant="outline" size="sm" disabled={ndPage * 25 >= nd.total} onClick={() => setNdPage(ndPage + 1)}>Next</Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---- RECYCLE BIN ---- */}
      <Card className="shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <p className="font-medium text-sm flex items-center gap-1.5" data-testid="recycle-bin-title">
                <ArchiveRestore className="h-4 w-4" /> Recycle bin {trash ? `(${trashTotal})` : ""}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {isSuper ? "Deleted properties can be restored with their data intact (inside the 30-day window)"
                  : "Restoring is Super Admin only — the full recycle bin (who / why / deleted via) is under Properties → Deleted properties"}
              </p>
            </div>
            {!readOnly && isSuper && <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => doRestore(false)} disabled={busy}
                data-testid="trash-restore-selected-button">
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore Selected
              </Button>
              <Button variant="outline" size="sm" onClick={() => doRestore(true)} disabled={busy || !trashTotal}
                data-testid="trash-restore-all-button">
                <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Restore All
              </Button>
            </div>}
          </div>
          {!trash ? <TableRowsSkeleton rows={5} cols={5} testId="trash-skeleton" /> : trash.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="trash-empty">Recycle bin is empty.</p>
          ) : (
            <div className={`border rounded-lg overflow-x-auto ${trashLoading ? "opacity-60 transition-opacity" : ""}`}>
              <Table data-testid="trash-table">
                <TableHeader>
                  <TableRow>
                    {isSuper && <TableHead className="w-10"></TableHead>}
                    <TableHead>PIN</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="hidden md:table-cell">Demands</TableHead>
                    <TableHead>Deleted by</TableHead>
                    <TableHead className="hidden md:table-cell">When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trash.map((t, i) => (
                    <TableRow key={t.id} data-testid={`trash-row-${i}`}>
                      {isSuper && (
                        <TableCell>
                          <Checkbox checked={!!trashSelected[t.id]}
                            onCheckedChange={(v) => setTrashSelected((s) => ({ ...s, [t.id]: !!v }))}
                            data-testid={`trash-check-${i}`} />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{t.pin}</TableCell>
                      <TableCell className="text-sm">{t.owner_name || "-"}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm tabular-nums">{t.demand_count || 0}</TableCell>
                      <TableCell className="text-xs">
                        <StaffLabel code={code} userId={t.deleted_by} name={t.deleted_by_name} fallback="-" />
                        <Badge variant="outline" className="ml-1.5 text-[10px]">
                          {t.deleted_via === "super_admin" ? "Super Admin" : "Admin"}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs">{fmtDate((t.deleted_at || "").slice(0, 10))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {trash && trashTotal > 0 && (
            <PaginationBar page={trashPage} limit={trashLimit} total={trashTotal} loading={trashLoading}
              onPageChange={setTrashPage} onLimitChange={(n) => { setTrashLimit(n); setTrashPage(1); }} testId="trash-pagination" />
          )}
        </CardContent>
      </Card>

      {/* delete confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="no-demand-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "all"
                ? `Delete ALL ${nd?.total ?? ""} properties without demand?`
                : `Delete ${ndSelectedCount} selected propert${ndSelectedCount === 1 ? "y" : "ies"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              They move to the recycle bin (not permanently erased) and can be restored anytime.
              You can then re-upload a corrected file for these properties.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="no-demand-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="no-demand-delete-confirm-button">
              {busy ? "Deleting..." : "Move to recycle bin"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* clear upload history confirmation */}
      <AlertDialog open={confirmClearHistory} onOpenChange={(v) => !v && setConfirmClearHistory(false)}>
        <AlertDialogContent data-testid="clear-upload-history-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear the entire upload batch history?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  This permanently removes all {batchTotal || ""} upload batch records (Excel and Third Party),
                  their per-row Created / Duplicate / Failed status records and the stored Excel files.
                  Use this to wipe test uploads before going live.
                </p>
                <p className="font-medium text-foreground">
                  Properties and demands already created from these uploads are NOT affected.
                </p>
                <p>This action is recorded in the audit log and cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-upload-history-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doClearHistory} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="clear-upload-history-confirm-button">
              {busy ? "Clearing..." : "Clear history"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
